// STARWEAVE — M1 client networking: connect the browser to the authoritative
// Durable-Object region (Spike C) and render peers + beacons on the planet
// surface (Spike B). The region uses 2D coordinates; we map them onto a
// tangent patch of the sphere at the play anchor.

import * as THREE from 'three';
import type { Planet } from './planet';

interface Peer { id: string; x: number; y: number; }

// Must mirror the server's beacon layout (server/src/index.ts).
const BEACONS = [
  { id: 0, x: 50, y: 50 },
  { id: 1, x: -60, y: 20 },
  { id: 2, x: 0, y: -70 },
];
const REGION_SCALE = 550; // region units per radian-ish across the patch
const CLAIM_RANGE = 30;

class RegionLink {
  ws: WebSocket;
  selfId: string | null = null;
  peers = new Map<string, Peer>();
  beacons = new Map<number, string | null>();
  connected = false;
  readonly phase = Math.random() * Math.PI * 2; // so two tabs move differently
  private seq = 0;

  constructor(url: string) {
    this.ws = new WebSocket(url);
    this.ws.addEventListener('open', () => { this.connected = true; this.send({ t: 'hello' }); });
    this.ws.addEventListener('error', () => { /* offline / single-player */ });
    this.ws.addEventListener('close', () => { this.connected = false; });
    this.ws.addEventListener('message', (e) => this.handle(JSON.parse(e.data as string)));
  }

  private handle(m: any): void {
    switch (m.t) {
      case 'welcome':
        this.selfId = m.id;
        for (const p of m.players) this.peers.set(p.id, p);
        for (const b of m.beacons) this.beacons.set(b.beacon, b.owner);
        break;
      case 'peer-join':
        this.peers.set(m.id, { id: m.id, x: m.x, y: m.y });
        break;
      case 'peer-leave':
        this.peers.delete(m.id);
        break;
      case 'state': {
        const p = this.peers.get(m.id);
        if (p) { p.x = m.x; p.y = m.y; } else this.peers.set(m.id, { id: m.id, x: m.x, y: m.y });
        break;
      }
      case 'claimed':
        this.beacons.set(m.beacon, m.owner);
        break;
    }
  }

  move(x: number, y: number): void { this.send({ t: 'move', x, y, seq: ++this.seq }); }
  claim(b: number): void { this.send({ t: 'claim', beacon: b }); }
  private send(m: unknown): void { if (this.ws.readyState === 1) this.ws.send(JSON.stringify(m)); }
}

export interface Multiplayer {
  tick(now: number): void;
  stats(): { players: number; owned: number; total: number; connected: boolean };
}

export function createMultiplayer(scene: THREE.Scene, getPlanet: () => Planet, anchor: THREE.Vector3, url: string, avatarTex: THREE.Texture): Multiplayer {
  const tangent = new THREE.Vector3().crossVectors(anchor, new THREE.Vector3(0, 1, 0)).normalize();
  const bitan = new THREE.Vector3().crossVectors(anchor, tangent).normalize();
  const lift = getPlanet().params.amplitude * 0.5 + 9;

  const toSurface = (x: number, y: number): THREE.Vector3 => {
    const dir = anchor.clone()
      .addScaledVector(tangent, x / REGION_SCALE)
      .addScaledVector(bitan, y / REGION_SCALE)
      .normalize();
    return getPlanet().surfacePoint(dir).addScaledVector(dir, lift);
  };

  // Beacons as octahedra.
  const beaconMeshes = new Map<number, THREE.Mesh>();
  for (const b of BEACONS) {
    const m = new THREE.Mesh(
      new THREE.OctahedronGeometry(8),
      new THREE.MeshStandardMaterial({ color: 0xcccccc, emissive: 0x555555, emissiveIntensity: 0.6, flatShading: true }),
    );
    m.position.copy(toSurface(b.x, b.y));
    scene.add(m);
    beaconMeshes.set(b.id, m);
  }

  // Avatars: camera-facing sprites using the generated character art.
  const AV_W = 16, AV_H = 22;
  const makeAvatar = (tint: number): THREE.Sprite => {
    const s = new THREE.Sprite(new THREE.SpriteMaterial({ map: avatarTex, color: tint, transparent: true, depthWrite: false }));
    s.scale.set(AV_W, AV_H, 1);
    return s;
  };
  const self = makeAvatar(0xcdfbff); // faint cyan = you
  scene.add(self);
  const peerMeshes = new Map<string, THREE.Sprite>();

  const link = new RegionLink(url);
  const lift2 = getPlanet().params.amplitude * 0.5 + AV_H * 0.5;
  let sx = 0, sy = 0;
  const claimed = new Set<number>();

  const place = (spr: THREE.Sprite, x: number, y: number) => {
    const dir = anchor.clone()
      .addScaledVector(tangent, x / REGION_SCALE)
      .addScaledVector(bitan, y / REGION_SCALE)
      .normalize();
    spr.position.copy(getPlanet().surfacePoint(dir)).addScaledVector(dir, lift2);
  };

  return {
    tick(now: number): void {
      // Drive self in a slow circle; the server validates each step.
      const a = now * 0.0004 + link.phase;
      sx = Math.cos(a) * 45;
      sy = Math.sin(a) * 45;
      place(self, sx, sy);
      if (link.connected) link.move(sx, sy);

      // Claim a beacon when in range (server is authoritative; ignores dupes).
      for (const b of BEACONS) {
        if (!claimed.has(b.id) && !link.beacons.get(b.id) && Math.hypot(b.x - sx, b.y - sy) <= CLAIM_RANGE) {
          link.claim(b.id);
          claimed.add(b.id);
        }
      }

      // Sync peer avatars.
      for (const [id, p] of link.peers) {
        if (id === link.selfId) continue;
        let m = peerMeshes.get(id);
        if (!m) { m = makeAvatar(0xffd9b0); scene.add(m); peerMeshes.set(id, m); }
        place(m, p.x, p.y);
      }
      for (const [id, m] of peerMeshes) {
        if (!link.peers.has(id) || id === link.selfId) { scene.remove(m); peerMeshes.delete(id); }
      }

      // Colour beacons by ownership.
      for (const [bid, m] of beaconMeshes) {
        const owner = link.beacons.get(bid);
        const mat = m.material as THREE.MeshStandardMaterial;
        if (!owner) { mat.color.set(0xcccccc); mat.emissive.set(0x555555); }
        else if (owner === link.selfId) { mat.color.set(0x33ddff); mat.emissive.set(0x33ddff); }
        else { mat.color.set(0xff7a1a); mat.emissive.set(0xff7a1a); }
      }
    },
    stats() {
      let owned = 0;
      for (const o of link.beacons.values()) if (o && o === link.selfId) owned++;
      return { players: link.peers.size, owned, total: BEACONS.length, connected: link.connected };
    },
  };
}
